import React, { FC, useId, useState } from 'react';
import styled from 'styled-components';

import OnOffSwitch from '@rover/components/src/buttons/OnOffSwitch/OnOffSwitch';
import Popover, { PopoverPlacement } from '@rover/components/src/callouts/Popover';
import { PopoverEvent } from '@rover/components/src/callouts/Popover/popover.constants';
import AlertInfo from '@rover/icons/svg/alert-info.svg';
import { Box, Flex, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import Modal from '@rover/react-lib/src/components/modals/Modal';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';

type ModalProps = {
  extraInfoContent: string;
  extraInfoTitle: string;
};

/** How the extra info opens on mobile. Desktop always uses the popover. */
export type ExtraInfoPresentation = 'modal' | 'bottomSheet';

type PopoverProps = {
  extraInfoContent: string;
  popoverPlacement?: PopoverPlacement;
  popoverEvent?: PopoverEvent;
};

type ExtraInfoProps = ModalProps &
  PopoverProps & {
    extraInfoPresentation?: ExtraInfoPresentation;
  };

type SwitchLabelProps = Partial<ExtraInfoProps> & { textContent: string; bigLabel?: boolean };

export type SwitchProps = SwitchLabelProps & {
  extraInfoContent?: string;
  textContent: string;
  onChange: (frequency: boolean) => void;
  on: boolean;
  bigLabel?: boolean;
};

// The popover content inherits label style without it.
const StyledPopover = styled(Popover)`
  font-weight: normal;
`;

export const PopoverAlertInfo: FC<PopoverProps> = ({
  extraInfoContent,
  popoverPlacement,
  popoverEvent,
}) => {
  const event = popoverEvent || PopoverEvent.HOVER;
  const placement = popoverPlacement || PopoverPlacement.BOTTOM;
  return (
    <Box mr="4x" ml="2x" flex="0 0 auto">
      <StyledPopover
        content={extraInfoContent}
        event={event}
        placement={placement}
        maxWidth="240px"
        display="block"
      >
        {/* The margin-top is necessary to counter the effect of popover wrapper. */}
        <AlertInfo width={14} height={14} style={{ marginTop: '6px' }} />
      </StyledPopover>
    </Box>
  );
};

const StyledModal = styled(Modal)`
  &.RoverModalContent {
    height: fit-content !important;
    padding: ${({ theme }) => theme.space['6x']} !important;
    border-radius: ${({ theme }) => theme.radii.secondary};
  }
  padding-top: 50%;
  padding-left: ${({ theme }) => theme.space['6x']};
  padding-right: ${({ theme }) => theme.space['6x']};
`;

const AlertInfoTrigger: FC<{ onPress: () => void }> = ({ onPress }) => (
  <Flex data-testid="alert-info-icon" onClick={onPress} ml="2x" flex="none">
    <AlertInfo width={14} height={14} />
  </Flex>
);

export const ModalAlertInfo: FC<ModalProps> = ({ extraInfoContent, extraInfoTitle }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = (): void => setIsOpen(true);
  const closeModal = (): void => setIsOpen(false);

  return (
    <>
      <AlertInfoTrigger onPress={openModal} />
      <StyledModal isOpen={isOpen} onRequestClose={closeModal} hasCloseButton withBorderRadius>
        <Heading size="300" testID="modal-alert-info-heading">
          {extraInfoTitle}
        </Heading>
        <Paragraph size="200">{extraInfoContent}</Paragraph>
      </StyledModal>
    </>
  );
};

// A centered modal opening from a bottom sheet reads as a different surface than the one
// it came from, so a switch living in a sheet shows its info in a sheet of its own.
export const BottomSheetAlertInfo: FC<ModalProps> = ({ extraInfoContent, extraInfoTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const headingId = useId();

  const openSheet = (): void => setIsOpen(true);
  const closeSheet = (): void => setIsOpen(false);

  return (
    <>
      <AlertInfoTrigger onPress={openSheet} />
      <BottomSheet
        isOpen={isOpen}
        onRequestClose={closeSheet}
        headerId={headingId}
        HeaderComponent={
          <Heading size="300" id={headingId} mb="0x" testID="sheet-alert-info-heading">
            {extraInfoTitle}
          </Heading>
        }
      >
        <Paragraph size="200">{extraInfoContent}</Paragraph>
      </BottomSheet>
    </>
  );
};

export const ExtraInfo: FC<ExtraInfoProps> = ({
  extraInfoContent,
  extraInfoTitle,
  extraInfoPresentation = 'modal',
  popoverEvent,
  popoverPlacement,
}) => {
  /* Additional information will show as a popover on desktop (triggered on hover by default) and modal on mobile
     (triggered on click). */
  if (isMobileBrowser()) {
    return extraInfoPresentation === 'bottomSheet' ? (
      <BottomSheetAlertInfo extraInfoContent={extraInfoContent} extraInfoTitle={extraInfoTitle} />
    ) : (
      <ModalAlertInfo extraInfoContent={extraInfoContent} extraInfoTitle={extraInfoTitle} />
    );
  }

  return (
    <PopoverAlertInfo
      extraInfoContent={extraInfoContent}
      popoverEvent={popoverEvent}
      popoverPlacement={popoverPlacement}
    />
  );
};

const SwitchLabel: FC<SwitchLabelProps> = ({
  extraInfoContent,
  extraInfoTitle,
  extraInfoPresentation,
  textContent,
  popoverEvent,
  popoverPlacement,
  bigLabel,
}) => {
  const shouldShowExtraInfo = !!(extraInfoContent && extraInfoTitle);
  const extraInfoWidth = shouldShowExtraInfo ? ['190px', 'none'] : 'none';

  return (
    <Flex justifyContent="left" alignItems="center">
      <Box flexGrow="0" flexShrink="1" maxWidth={extraInfoWidth} minWidth="0">
        <Heading
          size={bigLabel ? '200' : '100'}
          webProps={{ as: 'span' }}
          textColor="primary"
          fontWeight="semibold"
          mb="0x"
        >
          {textContent}
        </Heading>
      </Box>
      {shouldShowExtraInfo && (
        <ExtraInfo
          popoverEvent={popoverEvent}
          popoverPlacement={popoverPlacement}
          extraInfoContent={extraInfoContent}
          extraInfoTitle={extraInfoTitle}
          extraInfoPresentation={extraInfoPresentation}
        />
      )}
    </Flex>
  );
};

const SwitchWithText: FC<SwitchProps> = ({
  textContent,
  onChange,
  extraInfoContent,
  extraInfoTitle,
  extraInfoPresentation,
  popoverEvent,
  popoverPlacement,
  on,
  bigLabel,
}) => {
  return (
    <Flex justifyContent="space-between">
      <OnOffSwitch
        disabled={false}
        on={on}
        onToggle={(value): void => onChange(value)}
        label={
          <SwitchLabel
            textContent={textContent}
            popoverEvent={popoverEvent}
            popoverPlacement={popoverPlacement}
            extraInfoContent={extraInfoContent}
            extraInfoTitle={extraInfoTitle}
            extraInfoPresentation={extraInfoPresentation}
            bigLabel={bigLabel}
          />
        }
        hideLabel={false}
        inlineLabel
      />
    </Flex>
  );
};

export default SwitchWithText;
